<template>
  <div v-if="status !== 'pending'">
    <template v-if="showForm">
      <IdentityInformationsChoice
        v-show="step === 'choice'"
        v-model:id="id"
        :ids
      />
      <IdentityInformationsDetails
        v-show="step === 'details'"
        :id
        :form-id
        @submit="handleIdentityInformations"
      />
    </template>
    <div v-else>
      <p class="text-static-default-low body-1 mb-3 mt-4">
        {{ alreadySentTitle }}
      </p>
      <RevList class="mt-22" hasExternalBorders>
        <RevListItemStatic>
          <template #label>
            {{ alreadySentDescription }}
          </template>
          <template #description>
            {{ data?.idNumber }}
          </template>
        </RevListItemStatic>
      </RevList>
    </div>
  </div>
  <div v-else>
    <RevLoadingScreen :text="i18n(translationsKyc.loadingDetails)" />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import {
  PAYMENT_INFOS_OPTIONS,
  type PaymentInfo,
} from '@backmarket/http-api/src/api-specs-buyback/customer/client'
import type { FormValues } from '@backmarket/nuxt-layer-buyback/components/FormGenerator/FormGenerator.types'
import translations from '@backmarket/nuxt-layer-buyback/components/IdentityInformations/IdentityInformations.translations'
import { retrieveIds } from '@backmarket/nuxt-layer-buyback/components/IdentityInformations/IdentityInformations.utils'
import IdentityInformationsChoice from '@backmarket/nuxt-layer-buyback/components/IdentityInformations/components/IdentityInformationsChoice.vue'
import IdentityInformationsDetails from '@backmarket/nuxt-layer-buyback/components/IdentityInformations/components/IdentityInformationsDetails.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevList } from '@ds/components/List'
import { RevListItemStatic } from '@ds/components/ListItemStatic'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'

import { useIdentityInformations } from '~/scopes/dashboard/my-sales/composables/useKycInformations'

import translationsKyc from '../KycDetailsModal.translations'

const props = defineProps<{
  showForm: boolean
  formId: string
  step: 'choice' | 'details'
  identityInfos: PaymentInfo | undefined
}>()
const emit = defineEmits(['submit', 'last-step'])

const i18n = useI18n()
const { data, status } = useIdentityInformations()

const id = computed(() => {
  if (data?.value?.idType === 'driving_license') {
    return PAYMENT_INFOS_OPTIONS.DRIVING_LICENCE_NUMBER
  }

  if (data?.value?.idType === 'full_social_security') {
    return PAYMENT_INFOS_OPTIONS.SSN_NUMBER
  }

  return PAYMENT_INFOS_OPTIONS.SSN
})

if (props.identityInfos?.options.length === 1) {
  emit('last-step')
}

const ids = retrieveIds(i18n, props.identityInfos?.options)

function handleIdentityInformations(values: FormValues) {
  if (values.drivingLicense && typeof values.drivingLicense === 'string') {
    emit('submit', values.drivingLicense, 'driving_license')
  }
  if (values.ssn && typeof values.ssn === 'string') {
    emit('submit', values.ssn, 'social_security')
  }
  if (
    values.full_social_security &&
    typeof values.full_social_security === 'string'
  ) {
    emit('submit', values.full_social_security, 'full_social_security')
  }
}

const alreadySentTitle = computed(() => {
  switch (id.value) {
    case PAYMENT_INFOS_OPTIONS.DRIVING_LICENCE_NUMBER:
      return i18n(translations.alreadySentTitleDrivingLicense)
    case PAYMENT_INFOS_OPTIONS.SSN_NUMBER:
      return i18n(translations.alreadySentTitleFullSsn)
    case PAYMENT_INFOS_OPTIONS.SSN:
    default:
      return i18n(translations.alreadySentTitleSsn)
  }
})
const alreadySentDescription = computed(() => {
  switch (id.value) {
    case PAYMENT_INFOS_OPTIONS.DRIVING_LICENCE_NUMBER:
      return i18n(translations.alreadySentDescriptionDrivingLicense)
    case PAYMENT_INFOS_OPTIONS.SSN_NUMBER:
      return i18n(translations.alreadySentDescriptionFullSsn)
    case PAYMENT_INFOS_OPTIONS.SSN:
    default:
      return i18n(translations.alreadySentDescriptionSsn)
  }
})
</script>
